/**
 * Responsive `srcset` for the listing gallery's plain `<img>` tags.
 *
 * The gallery deliberately uses plain `<img>` (not `next/image`) to keep
 * the SSR/CSR byte diff at zero for hydration-sensitive crawlers — see the
 * comments in `Images.tsx`. The cost was that every visitor downloaded the
 * ORIGINAL S3 photo (972×729 up to 1600×1200) for a 410×240 mobile slot,
 * and the trade-off note said to revisit "if mobile LCP regresses". It did:
 * listing-page mobile LCP sat at 3.0 s p75 in Sep 2026 (target 2.5 s).
 *
 * This builds the same `/_next/image?url=…&w=…&q=75` candidates `next/image`
 * would emit, as ONE static attribute string — deterministic on both sides
 * of hydration, no injected style, no runtime re-render. `src` stays the
 * original URL as the fallback (and for Google Image indexing).
 *
 * Only S3 hosts registered in `next.config.js` → `images.remotePatterns`
 * are eligible; any other URL gets `undefined` so the browser just uses
 * `src` (the optimizer would 400 on an unregistered host and the `onError`
 * fallback would then hide a perfectly good photo).
 *
 * Widths MUST be members of `images.deviceSizes` ∪ `images.imageSizes` and
 * the quality a member of `images.qualities`, or the optimizer rejects the
 * request. Keep these in sync with `next.config.js`.
 */

/** Subset of `images.deviceSizes` that covers the gallery's rendered slots:
 *  mobile carousel 390–430 CSS px @2× ⇒ 828; desktop lead tile ~600 @1× ⇒
 *  640, @2× ⇒ 1200; desktop side tiles ~290 @2× ⇒ 640. */
export const OPTIMIZED_WIDTHS = [640, 828, 1080, 1200] as const

export const OPTIMIZED_QUALITY = 75

/**
 * Mirrors the S3 entries of `images.remotePatterns` in `next.config.js` —
 * PUBLIC buckets only. Private buckets never go through the optimizer (see
 * `isPrivateImageUrl` in src/utils/image.tsx).
 */
const OPTIMIZABLE_HOST =
  /^https:\/\/spotme-(?:public-)?images(?:-dev)?\.s3\.us-west-1\.amazonaws\.com\//

export const isOptimizableImageUrl = (url: string): boolean =>
  OPTIMIZABLE_HOST.test(url)

export const buildOptimizedSrcSet = (
  url: string | null | undefined
): string | undefined => {
  if (!url || !isOptimizableImageUrl(url)) return undefined
  const encoded = encodeURIComponent(url)
  return OPTIMIZED_WIDTHS.map(
    (w) => `/_next/image?url=${encoded}&w=${w}&q=${OPTIMIZED_QUALITY} ${w}w`
  ).join(', ')
}
